import Highcharts from 'highcharts/highstock';
import {
  DEFAULT_STORAGE_DURATION_H,
  DEFAULT_PUMPED_STORAGE_DURATION_H,
} from '../generationProfileInputsUtils';

/** Round-trip efficiency for pumped hydro (same convention as battery storage). */
const PUMPED_STORAGE_ROUND_TRIP_EFFICIENCY = 0.75;

/**
 * Hourly dispatch simulation: must-run baseload → RE → storage → hydro → merit order.
 * Returns null when required hourly profiles are missing or wrong length.
 */
export function runHourlyDispatchSimulation({
  hourlyProfile,
  solarProfile,
  windProfile,
  windOffProfile,
  nuclearProfile,
  bioCapacityGW,
  coalCapacityGW,
  geoCapacityGW,
  gasCapacityGW,
  oilCapacityGW,
  nuclearCapacityGW,
  solarCapacityGW,
  windCapacityGW,
  windOffCapacityGW,
  storageCapacityGW,
  storageDurationH,
  storageEfficiency,
  pumpedStorageCapacityGW,
  pumpedStorageDurationH,
  hydroCapacityGW,
  hydroMonthlyBudgetMWh,
  hydroIrenaCapacityGW,
  simulationYear,
  mustRunCF,
  voFlexDispatchStack,
}) {
const HOURS_PER_YEAR = 8760;
    const demand = hourlyProfile;
    const solar = solarProfile;
    const windOn = windProfile;
    const nuclear = nuclearProfile;
  
    /* -------------------- VALIDATION -------------------- */
    if (
      !demand ||
      !solar ||
      !windOn ||
      !nuclear ||
      demand.length !== HOURS_PER_YEAR ||
      solar.length !== HOURS_PER_YEAR ||
      windOn.length !== HOURS_PER_YEAR ||
      nuclear.length !== HOURS_PER_YEAR
    ) {
      return null;
    }
  
    const windOffArr = Array.isArray(windOffProfile) && windOffProfile.length === HOURS_PER_YEAR ? windOffProfile : new Float64Array(HOURS_PER_YEAR);
    
    /* -------------------- CONSTANTS -------------------- */
    const MW = 1000;
    const bioCapMW = (bioCapacityGW || 0) * MW;
    const coalCapMW = (coalCapacityGW || 0) * MW;
    const geoCapMW = (geoCapacityGW || 0) * MW;
    const gasCapMW = (gasCapacityGW || 0) * MW;
    const oilCapMW = (oilCapacityGW || 0) * MW;
    const isModelDispatch = Array.isArray(voFlexDispatchStack) && voFlexDispatchStack.length > 0;
    const bioMustRunFrac = isModelDispatch ? 0
      : (mustRunCF?.bioenergy?.utilization_factor != null ? 0.5 * mustRunCF.bioenergy.utilization_factor : 0);
    const coalMustRunFrac = isModelDispatch ? 0
      : (mustRunCF?.coal?.utilization_factor != null ? 0.5 * mustRunCF.coal.utilization_factor : 0);
    const geoMustRunFrac = 0.80;
    const bioMustRunMW = bioCapMW * bioMustRunFrac;
    const coalMustRunMW = coalCapMW * coalMustRunFrac;
    const geoMustRunMW = geoCapMW * geoMustRunFrac;
    const bioFlexCapMW = bioCapMW - bioMustRunMW;
    const coalFlexCapMW = coalCapMW - coalMustRunMW;
  
    /* -------------------- STORAGE (battery + pumped) -------------------- */
    const stCapMW = (storageCapacityGW || 0) * MW;
    const durationH = storageDurationH || DEFAULT_STORAGE_DURATION_H;
    const stEnergyMWh = stCapMW * durationH;
    const effBat = storageEfficiency || 1;

    const pumpedCapMW = (pumpedStorageCapacityGW || 0) * MW;
    const pumpedDurH = pumpedStorageDurationH || DEFAULT_PUMPED_STORAGE_DURATION_H;
    const pumpedEnergyMWh = pumpedCapMW * pumpedDurH;
    const effPumped = PUMPED_STORAGE_ROUND_TRIP_EFFICIENCY;

    /* -------------------- ARRAYS -------------------- */
    const residual1 = new Float64Array(HOURS_PER_YEAR);
    const residual2 = new Float64Array(HOURS_PER_YEAR);
    const storageCharge = new Float64Array(HOURS_PER_YEAR);
    const storageDischarge = new Float64Array(HOURS_PER_YEAR);
    const storageSoC = new Float64Array(HOURS_PER_YEAR + 1);
    const pumpedStorageCharge = new Float64Array(HOURS_PER_YEAR);
    const pumpedStorageDischarge = new Float64Array(HOURS_PER_YEAR);
    const pumpedStorageSoC = new Float64Array(HOURS_PER_YEAR + 1);
    const hydroSim = new Float64Array(HOURS_PER_YEAR);
    const bioMustRun = new Float64Array(HOURS_PER_YEAR).fill(bioMustRunMW);
    const coalMustRun = new Float64Array(HOURS_PER_YEAR).fill(coalMustRunMW);
    const geoMustRun = new Float64Array(HOURS_PER_YEAR).fill(geoMustRunMW);
    const bioFlex = new Float64Array(HOURS_PER_YEAR);
    const coalFlex = new Float64Array(HOURS_PER_YEAR);
    const gasFlex = new Float64Array(HOURS_PER_YEAR);
    const oilFlex = new Float64Array(HOURS_PER_YEAR);
    const unserved = new Float64Array(HOURS_PER_YEAR);
    const curtailed = new Float64Array(HOURS_PER_YEAR);
  
    /* -------------------- STEP 1 RESIDUAL -------------------- */
    for (let h = 0; h < HOURS_PER_YEAR; h++) {
      const mustRun = (nuclear[h] || 0) + bioMustRunMW + coalMustRunMW + geoMustRunMW;
      const re = (solar[h] || 0) + (windOn[h] || 0) + windOffArr[h];
      residual1[h] = (demand[h] || 0) - mustRun - re;
    }
    
    /* -------------------- STEP 2 STORAGE (pumped discharge before battery; charge battery before pumped) -------------------- */
    storageSoC[0] = stEnergyMWh * 0.5;
    pumpedStorageSoC[0] = pumpedEnergyMWh * 0.5;

    for (let h = 0; h < HOURS_PER_YEAR; h++) {
      let socB = storageSoC[h];
      let socP = pumpedStorageSoC[h];
      const r0 = residual1[h];

      if (r0 > 0) {
        let pumpedD = 0;
        if (pumpedCapMW > 0) {
          pumpedD = Math.min(r0, pumpedCapMW, socP * effPumped);
          socP -= pumpedD / effPumped;
        }
        const rAfterPumped = r0 - pumpedD;
        let batD = 0;
        if (stCapMW > 0) {
          batD = Math.min(rAfterPumped, stCapMW, socB * effBat);
          socB -= batD / effBat;
        }
        pumpedStorageDischarge[h] = pumpedD;
        storageDischarge[h] = batD;
        storageCharge[h] = 0;
        pumpedStorageCharge[h] = 0;
        residual2[h] = r0 - pumpedD - batD;
      } else if (r0 < 0) {
        let batC = 0;
        if (stCapMW > 0) {
          batC = Math.min(-r0, stCapMW, (stEnergyMWh - socB) / effBat);
          socB += batC * effBat;
        }
        const rAfterBattery = r0 + batC;
        let pumpedC = 0;
        if (pumpedCapMW > 0 && rAfterBattery < 0) {
          pumpedC = Math.min(-rAfterBattery, pumpedCapMW, (pumpedEnergyMWh - socP) / effPumped);
          socP += pumpedC * effPumped;
        }
        storageCharge[h] = batC;
        pumpedStorageCharge[h] = pumpedC;
        storageDischarge[h] = 0;
        pumpedStorageDischarge[h] = 0;
        residual2[h] = r0 + batC + pumpedC;
      } else {
        storageCharge[h] = 0;
        storageDischarge[h] = 0;
        pumpedStorageCharge[h] = 0;
        pumpedStorageDischarge[h] = 0;
        residual2[h] = 0;
      }

      storageSoC[h + 1] = socB;
      pumpedStorageSoC[h + 1] = socP;
    }
  
    /* -------------------- STEP 3 HYDRO -------------------- */
    if (hydroMonthlyBudgetMWh?.length === 12 && hydroCapacityGW > 0) {
      const hydroCapMW = hydroCapacityGW * MW;
      const scaleFactor = hydroIrenaCapacityGW > 0 ? hydroCapacityGW / hydroIrenaCapacityGW : 1;
      const year = simulationYear || 2022;
      let hour = 0;
  
      for (let m = 0; m < 12; m++) {
        const budget = (hydroMonthlyBudgetMWh[m] || 0) * scaleFactor;
        const days = new Date(year, m + 1, 0).getDate();
        const hours = days * 24;
        let positiveSum = 0;
  
        for (let i = 0; i < hours; i++) {
          const hIdx = hour + i;
          if (residual2[hIdx] > 0) positiveSum += residual2[hIdx];
        }
  
        for (let i = 0; i < hours; i++) {
          const hIdx = hour + i;

          if (budget > 0) {
            if (positiveSum > 0) {
                hydroSim[hIdx] = Math.min((residual2[hIdx] > 0 ? residual2[hIdx] / positiveSum * budget : 0), hydroCapMW);
            }
            else {
              hydroSim[hIdx] = Math.min(budget / hours, hydroCapMW);
            }
          }
        }

        // If hourly caps clipped the first pass, monthly hydro sum can fall short of budget.
        // Redistribute the remainder across positive-residual hours that still have headroom
        // so the monthly energy target is met when physically possible under hydroCapMW.
        if (budget > 0 && positiveSum > 0) {
          const monthStart = hour;
          for (let iter = 0; iter < 48; iter++) {
            let monthSum = 0;
            for (let i = 0; i < hours; i++) monthSum += hydroSim[monthStart + i];
            const deficit = budget - monthSum;
            if (deficit <= 0.01) break;

            let headSum = 0;
            for (let i = 0; i < hours; i++) {
              const hIdx = monthStart + i;
              if (residual2[hIdx] > 0) {
                headSum += Math.max(0, hydroCapMW - hydroSim[hIdx]);
              }
            }
            if (headSum < 1e-9) break;

            for (let i = 0; i < hours; i++) {
              const hIdx = monthStart + i;
              if (residual2[hIdx] <= 0) continue;
              const head = Math.max(0, hydroCapMW - hydroSim[hIdx]);
              if (head <= 0) continue;
              hydroSim[hIdx] += Math.min(head, deficit * (head / headSum));
            }
          }
        }

        hour += hours;
      }
    }
    
    /* -------------------- STEP 4 MERIT ORDER -------------------- */
    const fuelArrayMap = { bio: bioFlex, coal: coalFlex, gas: gasFlex, oil: oilFlex };

    /** @type {{ key: string, tech?: string, techId?: string, fuel: string, label?: string, cost: number, capMW: number }[]} */
    let meritOrder;
    /** @type {Record<string, number>} */
    const flexTechGenMWh = {};
    /** @type {Record<string, Float64Array>} */
    const flexTechHourly = {};

    if (isModelDispatch) {
      meritOrder = voFlexDispatchStack.map((row) => ({
        key: `${row.tech}|${row.fuel}|${row.techByFuel ?? ''}`,
        tech: row.tech,
        techId: row.techId || null,
        fuel: row.fuel,
        label: row.catalogLabel || row.tech,
        cost: row.costPerMwh,
        capMW: row.capacityGw * MW,
      }));
      for (const row of meritOrder) {
        flexTechGenMWh[row.key] = 0;
        flexTechHourly[row.key] = new Float64Array(HOURS_PER_YEAR);
      }
    } else {
      meritOrder = [
        { key: 'bio', fuel: 'bio', cost: 0, capMW: bioFlexCapMW },
        { key: 'coal', fuel: 'coal', cost: 1, capMW: coalFlexCapMW },
        { key: 'gas', fuel: 'gas', cost: 2, capMW: gasCapMW },
        { key: 'oil', fuel: 'oil', cost: 3, capMW: oilCapMW },
      ];
    }

    for (let h = 0; h < HOURS_PER_YEAR; h++) {
      let remaining = residual2[h] - hydroSim[h];

      if (remaining > 0) {
        for (const unit of meritOrder) {
          const cap = unit.capMW;
          if (cap <= 0) continue;
          const dispatched = Math.min(remaining, cap);
          if (dispatched <= 0) continue;
          fuelArrayMap[unit.fuel][h] += dispatched;
          if (unit.tech) {
            flexTechGenMWh[unit.key] = (flexTechGenMWh[unit.key] || 0) + dispatched;
            if (flexTechHourly[unit.key]) flexTechHourly[unit.key][h] += dispatched;
          }
          remaining -= dispatched;
          if (remaining <= 0) break;
        }
        if (remaining > 0) unserved[h] = remaining;
      } else if (remaining < 0) {
        curtailed[h] = -remaining;
      }
    }
  
    /* -------------------- METRICS -------------------- */
    let totalUnserved = 0;
    let totalCurtailed = 0;
    let hoursUnserved = 0;
    let hoursCurtailed = 0;
    let maxUnservedMW = 0;
    let maxHour = 0;
  
    for (let h = 0; h < HOURS_PER_YEAR; h++) {
      const u = unserved[h];
      const c = curtailed[h];
      totalUnserved += u;
      totalCurtailed += c;
  
      if (u > 0) hoursUnserved++;
      if (c > 0) hoursCurtailed++;
      if (u > maxUnservedMW) {
        maxUnservedMW = u;
        maxHour = h;
      }
    }
   
    const sum = arr => arr.reduce((s, v) => s + v, 0);
    const totalDemandTWh = sum(demand) / 1_000_000;
    const unservedTWh = totalUnserved / 1_000_000;
    const curtailedTWh = totalCurtailed / 1_000_000;

    // const yearStartMs = Date.UTC(formData?.year || 2022, 0, 1, 0, 0, 0);
    // const maxShortageTimestampMs = maxUnservedMW > 0 ? yearStartMs + maxHour * 3600000 : null;
    // const maxShortageTimestamp = maxUnservedMW > 0
    //   ? new Date(maxShortageTimestampMs).toLocaleString('en-GB', {
    //       day: '2-digit',
    //       month: 'short',
    //       year: 'numeric',
    //       hour: '2-digit',
    //       minute: '2-digit',
    //       hour12: false
    //     })
    //   : null;

    const year = simulationYear || 2022;
    const yearStartMs = Date.UTC(year, 0, 1);
    const maxShortageTimestampMs =
      maxUnservedMW > 0 ? yearStartMs + maxHour * 3600000 : null;
    const maxShortageTimestamp =
      maxUnservedMW > 0
        ? Highcharts.dateFormat('%B %d, %Y %H:%M', maxShortageTimestampMs)
        : null;

    const topMaxShortageCandidates = [];
    for (let h = 0; h < HOURS_PER_YEAR; h++) {
      const shortageMW = unserved[h];
      if (shortageMW > 0) {
        topMaxShortageCandidates.push({ hour: h, shortageMW });
      }
    }
    topMaxShortageCandidates.sort((a, b) => b.shortageMW - a.shortageMW);
    const topMaxShortageEvents = topMaxShortageCandidates
      .slice(0, 10)
      .sort((a, b) => a.hour - b.hour)
      .map(({ hour, shortageMW }) => {
        const timestampMs = yearStartMs + hour * 3600000;
        return {
          timestampMs,
          timestamp: Highcharts.dateFormat('%B %d, %Y %H:%M', timestampMs),
          shortage_GW: shortageMW / MW,
        };
      });

    // Per-fuel utilization (capacity factor) from simulation
    const fuelUtilization = {};
    const utilFuels = [
      { key: 'nuclear', label: 'Nuclear', genArr: nuclear, capGW: nuclearCapacityGW },
      { key: 'solar', label: 'Solar', genArr: solar, capGW: solarCapacityGW },
      { key: 'windOn', label: 'Wind (Onshore)', genArr: windOn, capGW: windCapacityGW },
      { key: 'windOff', label: 'Wind (Offshore)', genArr: windOffArr, capGW: windOffCapacityGW },
      { key: 'hydro', label: 'Hydro', genArr: hydroSim, capGW: hydroCapacityGW },
      { key: 'geo', label: 'Geothermal', genArr: geoMustRun, capGW: geoCapacityGW }
    ];
    // Dispatchable fuels: combine must-run + flex
    const bioTotal = new Float64Array(HOURS_PER_YEAR);
    const coalTotal = new Float64Array(HOURS_PER_YEAR);
    for (let h = 0; h < HOURS_PER_YEAR; h++) {
      bioTotal[h] = bioMustRun[h] + bioFlex[h];
      coalTotal[h] = coalMustRun[h] + coalFlex[h];
    }
    utilFuels.push(
      { key: 'bio', label: 'Bioenergy', genArr: bioTotal, capGW: bioCapacityGW },
      { key: 'coal', label: 'Coal', genArr: coalTotal, capGW: coalCapacityGW },
      { key: 'gas', label: 'Gas', genArr: gasFlex, capGW: gasCapacityGW },
      { key: 'oil', label: 'Oil', genArr: oilFlex, capGW: oilCapacityGW }
    );
    for (const { key, label, genArr, capGW } of utilFuels) {
      const capMW = (capGW || 0) * 1000;
      if (capMW <= 0) continue; // skip fuels with zero capacity
      let totalMWh = 0;
      for (let h = 0; h < HOURS_PER_YEAR; h++) totalMWh += (genArr[h] || 0);
      fuelUtilization[key] = {
        label,
        generationTWh: totalMWh / 1_000_000,
        capacityGW: capGW,
        utilization: totalMWh / (capMW * 8760)
      };
    }

    const storageChargeTWh = sum(storageCharge) / 1_000_000;
    const storageDischargeTWh = sum(storageDischarge) / 1_000_000;
    const pumpedStorageChargeTWh = sum(pumpedStorageCharge) / 1_000_000;
    const pumpedStorageDischargeTWh = sum(pumpedStorageDischarge) / 1_000_000;
    let totalEnergyTWh = storageDischargeTWh + pumpedStorageDischargeTWh;
    for (const fuel of Object.values(fuelUtilization)) {
      totalEnergyTWh += fuel.generationTWh;
    }

    /* -------------------- RESULT -------------------- */
    return {
      hydroSim: Array.from(hydroSim),
      bioMustRun: Array.from(bioMustRun),
      coalMustRun: Array.from(coalMustRun),
      geoMustRun: Array.from(geoMustRun),
      bioFlex: Array.from(bioFlex),
      coalFlex: Array.from(coalFlex),
      gasFlex: Array.from(gasFlex),
      oilFlex: Array.from(oilFlex),
      storageCharge: Array.from(storageCharge),
      storageDischarge: Array.from(storageDischarge),
      storageSoC: Array.from(storageSoC),
      pumpedStorageCharge: Array.from(pumpedStorageCharge),
      pumpedStorageDischarge: Array.from(pumpedStorageDischarge),
      pumpedStorageSoC: Array.from(pumpedStorageSoC),
      unserved: Array.from(unserved),
      curtailed: Array.from(curtailed),
      mustRunFractions: {
        bioenergy: bioMustRunFrac,
        coal: coalMustRunFrac,
        geothermal: geoMustRunFrac
      },
      dispatchMode: isModelDispatch ? 'vo-report-tech' : 'legacy',
      meritOrder: isModelDispatch
        ? voFlexDispatchStack.map((row) => row.tech)
        : meritOrder.map(({ key }) => key),
      flexDispatchOrder: isModelDispatch
        ? voFlexDispatchStack.map((row) => {
            const genKey = `${row.tech}|${row.fuel}|${row.techByFuel ?? ''}`;
            const genMWh = flexTechGenMWh[genKey] || 0;
            return {
              tech: row.tech,
              techId: row.techId || null,
              techByFuel: row.techByFuel,
              fuel: row.fuel,
              label: row.catalogLabel || row.tech,
              capacityGw: row.capacityGw,
              productionTwh: row.productionTwh,
              costPerMwh: row.costPerMwh,
              lastResort: Boolean(row.lastResort),
              generationTWh: Math.round((genMWh / 1_000_000) * 1e4) / 1e4,
              utilization:
                row.capacityGw > 0
                  ? genMWh / (row.capacityGw * 1000 * 8760)
                  : 0,
              hourlyMW: flexTechHourly[genKey] || null,
            };
          })
        : null,
      dispatchCosts: isModelDispatch
        ? Object.fromEntries(
            voFlexDispatchStack.map((row) => [row.tech, row.costPerMwh])
          )
        : null,
      fuelUtilization,
      balanceMetrics: {
        totalShortage_TWh: unservedTWh,
        shortagePercent: totalDemandTWh > 0 ? (unservedTWh / totalDemandTWh) * 100 : 0,
        maxShortage_GW: maxUnservedMW / MW,
        maxShortageTimestampMs,
        maxShortageTimestamp,
        topMaxShortageEvents,
        totalSurplus_TWh: curtailedTWh,
        totalEnergyTWh,
        curtailmentPercent:
          totalEnergyTWh > 0 ? (curtailedTWh / totalEnergyTWh) * 100 : 0,
        hoursUnserved,
        hoursCurtailed,
        storageChargeTWh,
        storageDischargeTWh,
        pumpedStorageChargeTWh,
        pumpedStorageDischargeTWh
      }
    };
}
